import { createContext, useContext, useState, useCallback } from "react";

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const showAlert = useCallback((message, type = "info", duration = 4000) => {
    const id = Math.random().toString(36).substring(2, 9);
    const alert = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      duration,
      timestamp: Date.now(),
    };

    setAlerts((prev) => [...prev, alert]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id);
      }, duration);
    }

    return id;
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback(
    (message, duration) => {
      return showAlert(message, "success", duration);
    },
    [showAlert]
  );

  const showError = useCallback(
    (message, duration) => {
      return showAlert(message, "error", duration);
    },
    [showAlert]
  );

  const showWarning = useCallback(
    (message, duration) => {
      return showAlert(message, "warning", duration);
    },
    [showAlert]
  );

  const showInfo = useCallback(
    (message, duration) => {
      return showAlert(message, "info", duration);
    },
    [showAlert]
  );

  const value = {
    alerts,
    showAlert,
    removeAlert,
    clearAllAlerts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
  );
};
